import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send } from "lucide-react";
import { API, WHATSAPP_URL, useApp, websiteChannelHeaders } from "../store";

const GENERIC_FALLBACK_PATTERNS = [
  /dificuld/i,
  /erro/i,
  /falh/i,
  /hiccup/i,
  /nao consegui/i,
  /não consegui/i,
  /assistance? connect/i,
  /please try again/i,
  /pick a session below directly/i,
];

function isGenericFallback(text = "") {
  return GENERIC_FALLBACK_PATTERNS.some((rx) => rx.test(text));
}

function fallbackReply(locale) {
  if (locale === "pt") {
    return "Posso te ajudar com diagnóstico, garantia, peças, manutenção e próximos passos. Se quiser, me diga o modelo e o sintoma.";
  }
  if (locale === "es") {
    return "Puedo ayudarte con diagnóstico, garantía, piezas, mantenimiento y próximos pasos. Si querés, decime el modelo y el síntoma.";
  }
  return "I can help with diagnostics, warranty, parts, maintenance, and next steps. If you'd like, tell me the model and the symptoms.";
}

function initialGreeting(locale) {
  if (locale === "pt") return "Oi! Eu sou o SharkX Support. Me diga o que aconteceu e eu te ajudo agora.";
  if (locale === "es") return "¡Hola! Soy SharkX Support. Contame qué pasó y te ayudo ahora mismo.";
  return "Hi! I'm SharkX Support. Tell me what happened and I'll help you right now.";
}

const SUPPORT_SESSION_STORAGE_KEY = "sharkx.website.support_session_id";

function getOrCreateSessionId(storageKey, prefix) {
  if (typeof window === "undefined") {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
  try {
    const existing = window.localStorage.getItem(storageKey);
    if (existing) return existing;
    const next = `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    window.localStorage.setItem(storageKey, next);
    return next;
  } catch {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
}

export default function FloatingSupportChat({ initialOpen = false }) {
  const { locale } = useApp();
  const [open, setOpen] = useState(initialOpen);
  const [messages, setMessages] = useState([{ role: "bot", text: initialGreeting(locale) }]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const sessionIdRef = useRef(getOrCreateSessionId(SUPPORT_SESSION_STORAGE_KEY, "support"));
  const scrollRef = useRef(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(SUPPORT_SESSION_STORAGE_KEY, sessionIdRef.current);
    } catch {
      // ignore storage failures
    }
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, thinking, open]);

  const title = useMemo(() => {
    if (locale === "pt") return "SharkX Support";
    if (locale === "es") return "SharkX Support";
    return "SharkX Support";
  }, [locale]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || thinking) return;

    const text = input.trim();
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setThinking(true);

    try {
      const res = await axios.post(
        `${API}/agent/support/chat`,
        {
          session_id: sessionIdRef.current,
          message: text,
          locale,
          country: "",
          channel_origin: "website-support",
        },
        websiteChannelHeaders("website-support")
      );
      const rawReply = res?.data?.reply || "";
      const reply = isGenericFallback(rawReply) ? fallbackReply(locale) : rawReply;
      setMessages((m) => [...m, { role: "bot", text: reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "bot", text: fallbackReply(locale) }]);
    } finally {
      setThinking(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full bg-emerald-500 text-white shadow-2xl shadow-emerald-500/40 grid place-items-center hover:bg-emerald-600 hover:scale-110 transition-all"
        aria-label="Open support chat"
        data-testid="floating-support-chat-btn"
      >
        <MessageCircle size={24} />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-[60] w-[360px] max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-5" data-testid="floating-support-chat-panel">
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white p-4 flex items-center justify-between">
        <div>
          <div className="font-ui font-bold text-sm tracking-wider">{title}</div>
          <div className="text-[10px] uppercase tracking-widest opacity-90">Support · Online</div>
        </div>
        <button onClick={() => setOpen(false)} className="hover:bg-white/20 rounded p-1" data-testid="floating-support-chat-close"><X size={18} /></button>
      </div>
      <div ref={scrollRef} className="flex-1 p-4 space-y-3 overflow-y-auto max-h-[360px] bg-slate-50" data-testid="floating-support-chat-messages">
        {messages.map((m, i) => (
          <div key={i} className={`text-sm rounded-2xl px-4 py-2 max-w-[85%] whitespace-pre-line ${m.role === "bot" ? "bg-white border border-slate-200 text-slate-800" : "bg-emerald-500 text-white ml-auto"}`}>
            {m.text}
          </div>
        ))}
        {thinking && (
          <div className="text-sm rounded-2xl px-4 py-2 max-w-[85%] bg-white border border-slate-200 text-slate-500" data-testid="floating-support-chat-thinking">
            <span className="inline-flex gap-1">
              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </span>
          </div>
        )}
      </div>
      <form onSubmit={send} className="border-t border-slate-200 p-3 flex gap-2 bg-white">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={locale === "pt" ? "Descreva o problema…" : locale === "es" ? "Describí el problema…" : "Describe the issue…"}
          className="flex-1 bg-transparent outline-none text-sm text-slate-900"
          data-testid="support-chat-input"
          disabled={thinking}
        />
        <button type="submit" disabled={thinking || !input.trim()} className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-full w-9 h-9 grid place-items-center" data-testid="support-chat-send"><Send size={14} /></button>
      </form>
      <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="bg-teal-500 hover:bg-teal-600 text-white py-3 text-sm font-ui font-semibold uppercase tracking-wider flex items-center justify-center gap-2" data-testid="support-chat-whatsapp">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.5 14.4c-.3-.2-1.8-.9-2-1s-.5-.2-.7.2-.8 1-.9 1.2c-.2.2-.3.2-.6.1-.3-.2-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5s-.7-1.6-.9-2.2c-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4s-1.1 1-1.1 2.5.9 2.9 1.1 3.1 2.2 3.4 5.4 4.7c.8.3 1.4.5 1.8.7.8.2 1.5.2 2 .1.6-.1 1.8-.7 2-1.5.3-.7.3-1.4.2-1.5-.1-.1-.3-.2-.6-.3zM12 2C6.5 2 2 6.5 2 12c0 1.9.5 3.7 1.5 5.3L2 22l4.8-1.5c1.6.9 3.3 1.4 5.2 1.4 5.5 0 10-4.5 10-10S17.5 2 12 2z"/></svg>
        Talk on WhatsApp
      </a>
    </div>
  );
}
